import React, { useState } from 'react';

/**
 * UserUploadFile component for handling file uploads with consistent UI
 * @param {Object} props - Component props
 * @param {string} props.fileType - The type of file being uploaded (e.g., 'idFront', 'idBack', 'profileImage')
 * @param {Object} props.uploadedFile - The currently uploaded file data
 * @param {Function} props.onFileUpload - Callback function when file is uploaded
 * @param {Function} props.onFileRemove - Callback function when file is removed
 * @param {string} props.label - Label text for the upload field
 * @param {string} props.icon - FontAwesome icon class for the upload field
 * @param {string} props.uploadText - Text for the upload link
 * @param {string} props.subtitle - Subtitle text for the upload area
 * @param {string} props.accept - File types to accept (default: "image/*")
 * @param {string} props.sizeText - File size limit text
 * @param {boolean} props.isPassport - Whether this is for passport upload (shows different icon)
 * @returns {JSX.Element} The UserUploadFile component
 */
const UserUploadFile = ({
  fileType,
  uploadedFile,
  onFileUpload,
  onFileRemove,
  label,
  label2,
  icon,
  uploadText,
  subtitle = "or drag and drop",
  accept = "image/jpeg, image/jpg, image/png",
  sizeText = "PNG, JPG up to 10MB",
  isPassport = false,
  isRequired = false
}) => {
  const inputId = `${fileType}-upload`;
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    onFileUpload(e, fileType);
  };

  const handleRemoveFile = () => {
    onFileRemove(fileType);
  };

  const handleButtonClick = () => {
    document.getElementById(inputId).click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Create a synthetic event object that matches the expected structure
      const syntheticEvent = {
        target: {
          files: files
        }
      };
      onFileUpload(syntheticEvent, fileType);
    }
  };

  return (
    <div className="upload-field">
      <label className={`upload-field-label ${label2 ? 'mb-1' : ''}`}>
        <i className={`upload-field-icon ${icon}`}></i>
        {label} {isRequired && <span className="modern-field-required">*</span>}
      </label>
      <p className='c_gray'><small>{label2}</small></p>

      {!uploadedFile ? (
        <>
          <div className="upload-container">
            <div 
              className={`upload-area ${isDragging ? 'dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="upload-content">
                <div className="upload-icon floating-animation">
                  {isPassport ? (
                    <svg className='svg-inline--fa' viewBox="0 0 448 512">
                      <path fill="#fff" d="M0 64C0 28.7 28.7 0 64 0H384c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zM183 278.8c-27.9-13.2-48.4-39.4-53.7-70.8h39.1c1.6 30.4 7.7 53.8 14.6 70.8zm41.3 9.2l-.3 0-.3 0c-2.4-3.5-5.7-8.9-9.1-16.5c-6-13.6-12.4-34.3-14.2-63.5h47.1c-1.8 29.2-8.1 49.9-14.2 63.5c-3.4 7.6-6.7 13-9.1 16.5zm40.7-9.2c6.8-17.1 12.9-40.4 14.6-70.8h39.1c-5.3 31.4-25.8 57.6-53.7 70.8zM279.6 176c-1.6-30.4-7.7-53.8-14.6-70.8c27.9 13.2 48.4 39.4 53.7 70.8H279.6zM223.7 96l.3 0 .3 0c2.4 3.5 5.7 8.9 9.1 16.5c6 13.6 12.4 34.3 14.2 63.5H200.5c1.8-29.2 8.1-49.9 14.2-63.5c3.4-7.6 6.7-13 9.1-16.5zM183 105.2c-6.8 17.1-12.9 40.4-14.6 70.8H129.3c5.3-31.4 25.8-57.6 53.7-70.8zM352 192A128 128 0 1 0 96 192a128 128 0 1 0 256 0zM112 384c-8.8 0-16 7.2-16 16s7.2 16 16 16H336c8.8 0 16-7.2 16-16s-7.2-16-16-16H112z" />
                    </svg>
                  ) : fileType === 'passportSelfie' ? (
                    <svg className="svg-inline--fa" viewBox="0 0 320 512">
                      <path fill="#fff" d="M112 48a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm40 304V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V256.9L59.4 304.5c-9.1 15.1-28.8 20-43.9 10.9s-20-28.8-10.9-43.9l58.3-97c17.4-28.9 48.6-46.6 82.3-46.6h29.7c33.7 0 64.9 17.7 82.3 46.6l58.3 97c9.1 15.1 4.2 34.8-10.9 43.9s-34.8 4.2-43.9-10.9L232 256.9V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V352H152z" />
                    </svg>
                  ) : (
                    <i style={{ height: "1.7rem", width: "1.7rem" }} className={icon}></i>
                  )}
                </div>
                <div className="upload-text">
                  <label htmlFor={inputId} className="upload-link">
                    <span>{uploadText}</span>
                    <input
                      id={inputId}
                      name={inputId}
                      type="file"
                      accept={accept}
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                  </label>
                  <p className="upload-subtitle">{subtitle}</p>
                </div>
                <p className="upload-size">{sizeText}</p>
              </div>
            </div>
          </div>

          <div className="upload-button-container">
            <button
              type="button"
              onClick={handleButtonClick}
              className="upload-btn"
            >
              <i style={{ color: "#a3a3a3" }} className="fas fa-folder-open"></i>
              <span>Choose File</span>
            </button>
          </div>
        </>
      ) : (
        <div className="file-preview">
          <div className="file-preview-header">
            <span className="file-preview-title">
              <i className="fas fa-image"></i>
              {label}
            </span>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="file-remove-btn"
            >
              <i className="mr-1 fas fa-trash"></i>Remove
            </button>
          </div>
          <img src={uploadedFile.preview} alt={label} className="file-preview-image" />
        </div>
      )}
    </div>
  );
};


export default UserUploadFile;
